import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Download, Upload } from 'lucide-react';
import DataTable from '../common/DataTable';
import SupplierForm from './SupplierForm';
import ConfirmationModal from '../common/ConfirmationModal';
import { inventoryService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmation } from '../../hooks/useConfirmation';
import { Supplier } from '../../types';

const SupplierManagement: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | undefined>(undefined);
  const [resetForm, setResetForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast } = useToast();
  const { confirmationState, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  useEffect(() => {
    loadSuppliers(1);
  }, []);

  const loadSuppliers = async (page: number, search?: string) => {
    try {
      setLoading(true);
      const searchText = search !== undefined ? search : searchTerm;
      const response = await inventoryService.getSuppliers({ 
        page, 
        per_page: 10, 
        search: searchText 
      });
      setSuppliers(response.data);
      setTotalItems(response.total || 0);
      setCurrentPage(response.page || page);
      if (search !== undefined) {
        setSearchTerm(search);
      }
    } catch (error) {
      showToast('error', 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (search: string) => {
    setSearchTerm(search);
    if (search) {
      await loadSuppliers(1, search);
    } else {
      await loadSuppliers(1, '');
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
  };

  const handleSave = async (supplierData: any) => {
    try {
      if (editingSupplier) {
        await inventoryService.updateSupplier(editingSupplier.id, supplierData);
        showToast('success', 'Supplier updated successfully');
      } else {
        await inventoryService.createSupplier(supplierData);
        showToast('success', 'Supplier created successfully');
      }
      setEditingSupplier(undefined);
      setResetForm(true);
      setTimeout(() => setResetForm(false), 100);
      loadSuppliers(currentPage);
    } catch (error) {
      showToast('error', 'Failed to save supplier');
    }
  };

  const handleCancel = () => {
    setEditingSupplier(undefined);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const downloadTemplate = async () => {
    try {
      const blob = await inventoryService.downloadSuppliersTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'suppliers_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('success', 'Template downloaded successfully');
    } catch (error) {
      showToast('error', 'Failed to download template');
    }
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      await inventoryService.importSuppliers(file);
      showToast('success', 'Suppliers imported successfully');
      loadSuppliers(1);
    } catch (error) {
      showToast('error', 'Failed to import suppliers');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleToggleCollapse = () => {
    setIsFormCollapsed(!isFormCollapsed);
  };

  const handleDelete = async (supplier: Supplier) => {
    showConfirmation(
      {
        title: 'Delete Supplier',
        message: `Are you sure you want to delete supplier "${supplier.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        variant: 'danger'
      },
      async () => {
        try {
          await inventoryService.deleteSupplier(supplier.id);
          showToast('success', 'Supplier deleted successfully');
          loadSuppliers(currentPage);
        } catch (error) {
          showToast('error', 'Failed to delete supplier');
        }
      }
    );
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { 
      key: 'email', 
      label: 'Email',
      render: (value: string) => value || '-'
    },
    { 
      key: 'tax_id', 
      label: 'Tax ID',
      render: (value: string) => value || '-'
    },
    { 
      key: 'contact_person', 
      label: 'Contact Person',
      render: (value: string) => value || '-'
    },
    { 
      key: 'address', 
      label: 'Address',
      render: (value: string) => (
        <span className="text-xs" title={value}>
          {value && value.length > 30 ? `${value.substring(0, 30)}...` : value || '-'}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
          <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>
            {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </h2>
          <div className="flex items-center" style={{ gap: 'var(--erp-spacing-sm)' }}>
            <button
              type="button"
              onClick={downloadTemplate}
              className="erp-form-btn erp-btn-template"
            >
              <Download className="erp-form-btn-icon" />
              Template
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="erp-form-btn erp-btn-import"
            >
              <Upload className="erp-form-btn-icon" />
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileImport}
              className="hidden"
            />
            <button
              type="button"
              onClick={handleToggleCollapse}
              className="text-gray-500 hover:text-gray-700"
            >
              {isFormCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>

        {!isFormCollapsed && (
          <SupplierForm
            supplier={editingSupplier}
            onSave={handleSave}
            onCancel={handleCancel}
            isCollapsed={false}
            onToggleCollapse={() => {}}
            resetForm={resetForm}
          />
        )}
      </div>
      
      <DataTable
        title="Supplier Management"
        columns={columns}
        data={suppliers}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        pageSize={10}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={(page) => loadSuppliers(page)}
        onSearch={handleSearch}
      />
      
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        variant={confirmationState.variant}
        onConfirm={handleConfirm}
        onCancel={hideConfirmation}
      />
    </div>
  );
};

export default SupplierManagement;